import { create } from 'zustand';
import { Dog, DogSearchFilter, Gender } from '@/types/dog';
import { dogService } from '@/services/firebase/dogService';

/**
 * 個体管理ストアの状態型定義
 */
interface DogState {
  // 状態
  dogs: Dog[];
  selectedDog: Dog | null;
  loading: boolean;
  error: string | null;
  searchFilter: DogSearchFilter;

  // アクション
  setDogs: (dogs: Dog[]) => void;
  setSelectedDog: (dog: Dog | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSearchFilter: (filter: DogSearchFilter) => void;
  clearSearchFilter: () => void;

  // API連携アクション
  fetchDogs: (userId: string, gender?: Gender) => Promise<void>;
  fetchDog: (dogId: string, userId: string) => Promise<void>;
  searchDogs: (userId: string) => Promise<void>;
  deleteDog: (dogId: string, userId: string, permanent?: boolean) => Promise<void>;
}

/**
 * 個体管理ストア
 */
export const useDogStore = create<DogState>((set, get) => ({
  // 初期状態
  dogs: [],
  selectedDog: null,
  loading: false,
  error: null,
  searchFilter: {},

  // アクション
  setDogs: (dogs) => set({ dogs }),

  setSelectedDog: (dog) => set({ selectedDog: dog }),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  setSearchFilter: (filter) => set({ searchFilter: filter }),

  clearSearchFilter: () => set({ searchFilter: {} }),

  /**
   * 個体一覧取得
   */
  fetchDogs: async (userId: string, gender?: Gender) => {
    set({ loading: true, error: null });

    try {
      const dogs = await dogService.getDogs(
        userId,
        gender ? { gender } : undefined,
        'birthDate',
        'desc'
      );

      set({ dogs, loading: false });
    } catch (error) {
      console.error('個体一覧取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : '個体一覧の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 個体詳細取得
   */
  fetchDog: async (dogId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      const dog = await dogService.getDog(dogId, userId);

      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      set({ selectedDog: dog, loading: false });
    } catch (error) {
      console.error('個体詳細取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : '個体詳細の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 個体検索
   */
  searchDogs: async (userId: string) => {
    set({ loading: true, error: null });

    try {
      const { searchFilter } = get();
      const dogs = await dogService.searchDogs(userId, searchFilter);

      set({ dogs, loading: false });
    } catch (error) {
      console.error('個体検索エラー:', error);
      set({
        error: error instanceof Error ? error.message : '個体検索に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 個体削除
   */
  deleteDog: async (dogId: string, userId: string, permanent = false) => {
    set({ loading: true, error: null });

    try {
      await dogService.deleteDog(dogId, userId, permanent);

      // 一覧から削除した個体を除外
      const { dogs } = get();
      const updatedDogs = dogs.filter((dog) => dog.id !== dogId);

      set({ dogs: updatedDogs, selectedDog: null, loading: false });
    } catch (error) {
      console.error('個体削除エラー:', error);
      set({
        error: error instanceof Error ? error.message : '個体の削除に失敗しました',
        loading: false,
      });
    }
  },
}));
